/**
 * TreemapRenderer — hierarchy rollup with built-in drill + breadcrumbs.
 *
 * Best for "where did this $1.2B go?" rollups (agency → MAJCOM → office,
 * PSC L1 → L2). Replaces stacked-bar where ≥4 categories make bars cramped
 * and where leaf size matters more than ordinal rank.
 *
 * Phase C2 of issue #90 v3 plan.
 *
 * Note: ECharts treemap drill is built-in (`leafDepth` + `roam: false` + click
 * navigates levels). For our flat one-level rollups, nodes are leaves; the
 * caller can pass nested children to enable real drilling, but a single-level
 * treemap is the most common case (this renderer accepts both shapes).
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, getDimensionLabel, getDimensionKey } from './types'
import { useExecChartTheme } from './echarts-theme'

interface TreemapNode {
  name: string
  value: number
  /** Optional child dimension code for drill-down. */
  code?: string
  children?: TreemapNode[]
}

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  onDrillDown?: (code: string, label: string) => void
  /** Optional pre-built nested tree (overrides flat conversion from `data`). */
  tree?: TreemapNode[]
}

export default function TreemapRenderer({ data, groupBy, metrics, onDrillDown, tree }: Props) {
  const theme = useExecChartTheme()
  const metric = metrics[0]

  const nodes = useMemo<TreemapNode[]>(() => {
    if (tree) return tree
    return data.map((r) => {
      const v = r[metric]
      return {
        name: getDimensionLabel(r, groupBy),
        value: typeof v === 'number' ? v : parseFloat(String(v)) || 0,
        code: getDimensionKey(r, groupBy),
      }
    })
  }, [data, tree, groupBy, metric])

  if (nodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const option: any = {
    tooltip: {
      formatter: (params: any) => {
        const v = params?.value
        const formatted = typeof v === 'number' ? formatMetric(metric, v) : '—'
        return `<b>${params.name}</b><br/>${METRIC_LABELS[metric]}: <b>${formatted}</b>`
      },
    },
    series: [
      {
        type: 'treemap',
        data: nodes,
        roam: false,
        nodeClick: 'zoomToNode',
        // Fill the chart area edge-to-edge — defaults reserve ~60px for a
        // title we don't set, leaving an empty band at the top of the tile.
        top: 4,
        left: 4,
        right: 4,
        bottom: 24,
        breadcrumb: {
          show: true,
          // Pin the hover breadcrumb to the bottom inset so it doesn't
          // float over a leaf.
          bottom: 0,
          // textStyle is a sibling of itemStyle in ECharts breadcrumb; nesting
          // it inside itemStyle is what crashed the renderer.
          textStyle: { fontSize: 10 },
        },
        label: {
          show: true,
          formatter: (params: any) => {
            const v = params?.value
            const formatted = typeof v === 'number' ? formatMetric(metric, v) : ''
            return `${params.name}\n${formatted}`
          },
          fontSize: 11,
        },
        // Per-level styling. Always provide enough entries so ECharts doesn't
        // try to read past the array — the "Cannot set properties of undefined
        // (setting '2')" crash happens when a node has children deeper than
        // the levels array provides.
        levels: [
          { itemStyle: { borderColor: 'rgba(0,0,0,0.15)', borderWidth: 1, gapWidth: 1 } },
          { itemStyle: { gapWidth: 1 }, colorSaturation: [0.3, 0.6] },
          { itemStyle: { gapWidth: 1 }, colorSaturation: [0.3, 0.5] },
        ],
      },
    ],
  }

  const onEvents = onDrillDown
    ? {
        click: (params: any) => {
          const code = params?.data?.code
          const name = params?.name
          if (code && name) onDrillDown(code, name)
        },
      }
    : undefined

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
    />
  )
}
